import type { ReactNode } from "react";
import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { extractPaymentMethodId } from "@/Shared/Utils/stripeUtils";
import { BankAccountsAdviceDialog } from "../Dialogs/BankAccountsAdviceDialog";

/**
 * Add-bank-account workflow: shows the manually-verify advice dialog, then
 * runs the Stripe ACSS debit setup. Declarative — render the returned
 * `dialogs`; call `start()` to begin.
 */
export function useBankAccountsAddNew(
  refresh: (newPaymentMethodId: string | undefined) => Promise<void>,
): { start: () => Promise<void>; dialogs: ReactNode } {
  const stripe = useStripe();
  const paymentMethodsApi = usePaymentMethodsApi();
  const { user } = useAuthentication();

  const [adviceOpen, setAdviceOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const [errorNotice, setErrorNotice] = useState<string | undefined>();

  async function start() {
    if (!stripe) {
      console.error("stripe is null");
      return;
    }

    const response = await paymentMethodsApi.generateSaveBankAccountIntent();
    setClientSecret(response.clientSecret);
    setAdviceOpen(true);
  }

  async function runStripeFlow() {
    setAdviceOpen(false);

    if (!stripe || clientSecret === undefined) {
      console.error("stripe or clientSecret missing");
      return;
    }

    const result = await stripe.confirmAcssDebitSetup(clientSecret, {
      payment_method: {
        billing_details: {
          name: user?.name ?? "",
          email: user?.email ?? "",
        },
      },
    });

    if (result.error) {
      if (result.error.message !== "Please provide complete payment details.") {
        setErrorNotice(result.error.message);
      }

      refresh(undefined);
    } else {
      // Micro-deposit ("manually verify") accounts come back as requires_action:
      // the PaymentMethod exists but isn't attached to the customer yet, so we
      // must NOT try to make it the default — that 409s on the backend and would
      // abort the list refresh. Treat them like "no new default": just reload so
      // the pending row appears immediately.
      const isPendingVerification =
        result.setupIntent?.status === "requires_action";
      if (!isPendingVerification && result.setupIntent?.payment_method) {
        refresh(extractPaymentMethodId(result.setupIntent.payment_method));
      } else {
        refresh(undefined);
      }
    }
  }

  const dialogs: ReactNode = (
    <>
      <BankAccountsAdviceDialog
        open={adviceOpen}
        onOpenChange={setAdviceOpen}
        onGotIt={runStripeFlow}
      />
      <Dialog
        open={errorNotice !== undefined}
        onOpenChange={(open) => {
          if (!open) setErrorNotice(undefined);
        }}
        title="Error"
        footer={
          <Button primary onClick={() => setErrorNotice(undefined)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {errorNotice}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return { start, dialogs };
}
